/** In this file entities are declared */
import { BASE_URL } from "../../request";
import ModuleTranslations from "./ModuleTranslations";
import { getTranslatedDefinition } from "../utils";
import {
  ADDON_PAYMENTS_METHODS,
  ADDON_PAYMENTS_METHODS_LABEL,
  ADYEN_METHODS,
  ADYEN_METHODS_LABEL,
  CHECKOUT_PNP_PAYMENTS_METHODS,
  CHECKOUT_PNP_PAYMENTS_METHODS_LABEL,
  CODE_TYPE,
  DEVICE_TYPE,
  FREEDOM_PAY_PAYMENTS_METHODS,
  FREEDOM_PAY_PAYMENTS_METHODS_LABEL,
  PAYCOMET_PAYMENTS_METHODS,
  PAYCOMET_PAYMENTS_METHODS_LABEL,
  PAYMENT_TYPES,
  PAYMENT_TYPES_NAMES,
  PNP_SDK_PAYMENTS_METHODS,
  PNP_SDK_PAYMENTS_METHODS_LABEL,
  RESTRICTION_TYPE,
} from "../../Common/constants";
import { CreateConnectedAccountBtn } from "./stripe/CreateConnectedAccountBtn";
import { CreateAccountLinkBtn } from "./stripe/CreateAccountLinkBtn";
import { FormattedMessage } from "react-intl";
import React from "react";
import { messages } from "../../globalVariables";
import { StripeAccountTypeSelect } from "./stripe/StripeAccountTypeSelect";

const ModuleDefinition = {
  /** name is the title */
  name: "Payment Types",
  /** entityName is used to link with the constants */
  entityName: "paymentTypes",
  restConfig: {
    baseURL: `${BASE_URL}`,
    create: {
      url: ({ hotelTicker }) => `/api/backoffice/${hotelTicker}/payments`,
      method: "post",
      resetCache: true,
    },
    update: {
      url: ({ propertyTicker }) => `/api/backoffice/${propertyTicker}/payments`,
      method: "put",
      resetCache: true,
    },
    fetch: {
      url: ({ propertyTicker, id }) =>
        id
          ? `/api/backoffice/${propertyTicker}/payments/${id}`
          : `/api/backoffice/${propertyTicker}/payments`,
      method: "get",
    },
    delete: {
      url: ({ hotelTicker, id }) => `/api/backoffice/${hotelTicker}/payments/${id}`,
      method: "delete",
      resetCache: true,
    },
  },
  renderers: {
    list: {},
    form: {},
  }, //render functions by attribute name, this overrides default widgets specified in the fields section
  translation: {
    translated: true,
    fieldName: "translations",
    displayName: "Translations",
    widget: "translations",
    entityProperty: "translations",
    localeProperty: "language",
  },
  fields: {
    /** Here are te entities used in the lists, Order is important */
    /** fieldName: this is used like the id container of the properties */
    /** displayName: this is the title rendered */
    list: [
      {
        fieldName: "type",
        displayName: "Type",
      },
      {
        fieldName: "title",
        displayName: "Title",
      },
      {
        fieldName: "minRelease",
        widget: "durationText",
        displayName: "Min. release (hrs)",
      },
      {
        fieldName: "maxRelease",
        widget: "durationText",
        displayName: "Max. release (hrs)",
      },
    ],
    /** Here is the entity used in the forms, Order is important */
    /** all the properties can be found in app/components/CRUD/Common, at the corresponding js file */
    /** fieldName: is like the id in html, it must be unique among the attributes */
    /** widget: is used to render the kind of interaction the user will have, a complete list can be found in component/CRUD/Common */
    /** displayName: The text that introduces the widget */
    /** defaultValue: The default value of the widget */
    /** optionDisplay: An object that containes objects with the option at the select  */
    form: [
      {
        fieldName: "name",
        className: "wbkv9-title",
        widget: "section",
        transform: ({ locale, entity, field }) => ({
          ...field,
          displayName:
            PAYMENT_TYPES_NAMES &&
            PAYMENT_TYPES_NAMES[locale] &&
            PAYMENT_TYPES_NAMES[locale][entity.type],
        }),
      },
      {
        fieldName: "type",
        widget: "hidden",
      },
      {
        fieldName: "merchantId",
        displayName: "Merchant Id",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADDON_PAYMENTS,
      },
      {
        fieldName: "merchantProductId",
        displayName: "Merchant Product Id",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADDON_PAYMENTS,
      },
      {
        fieldName: "merchantPassword",
        displayName: "Merchant Password",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADDON_PAYMENTS,
      },
      {
        fieldName: "merchantAccount",
        displayName: "Merchant Account",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADYEN,
      },
      {
        fieldName: "publicApiKey",
        displayName: "Client Key",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADYEN,
      },
      {
        fieldName: "secret",
        displayName: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.CRIPTAN ? "Secret" : "Key",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.PAYPAL_EXPRESS_CHECKOUT ||
          formValues.type === PAYMENT_TYPES.PAYPAL_EXPRESS_STANDARD ||
          formValues.type === PAYMENT_TYPES.CRIPTAN,
      },
      {
        fieldName: "pnpClient",
        displayName: "Client",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PNP_TOKEN,
      },
      {
        fieldName: "pnpGateway",
        displayName: "Payment letter",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PNP_TOKEN,
      },
      {
        fieldName: "key",
        displayName: "Clave",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PNP_TOKEN,
      },
      {
        fieldName: "clave256",
        displayName: "Encryption key",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "Ds_Merchant_MerchantCode",
        displayName: "MerchantCode",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "Ds_Merchant_Terminal",
        displayName: "MerchantTerminal",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "Ds_Merchant_Currency",
        displayName: "MerchantCurrency",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "Ds_Merchant_MerchantURL",
        widget: "hidden",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "allowTokenize",
        displayName: "Allow tokenization",
        widget: "checkbox",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "url",
        displayName: "Url",
        widget: "input",
        helpText:
          'Url test "https://sis-t.redsys.es:25443/sis/realizarPago" / producción https://sis.redsys.es/sis/realizarPago',
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.POS,
      },
      {
        fieldName: "clientId",
        displayName: "Client",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PAYPAL_EXPRESS_CHECKOUT,
      },
      {
        fieldName: "pci_proxy_channel",
        displayName: "PCI Proxy Channel",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PCI_PROXY,
      },
      {
        fieldName: "accountType",
        widget: "custom",
        defaultValue: "-",
        options: [
          {
            value: "-",
            label: "-",
          },
          {
            value: "NEW",
            label: "Create new account",
          },
          {
            value: "EXISTING",
            label: "Link Septeo/Tesipro account",
          },
        ],
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.STRIPE,
        customRenderer: ({ field, formValues, updateFormValue }) => (
          <StripeAccountTypeSelect
            options={field.options}
            type={formValues.accountType}
            disabled={Boolean(formValues.accountId)}
            updateFormValue={updateFormValue}
          />
        ),
      },
      {
        fieldName: "stripeAccountButton",
        widget: "custom",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.STRIPE &&
          !formValues.accountId &&
          formValues.accountType === "NEW",
        customRenderer: ({ field, entity, updateFormValue }) => (
          <CreateConnectedAccountBtn
            label={field.displayBtn}
            updateFormValue={updateFormValue}
            paymentMethod={entity.ticker}
            propertyTicker={entity.propertyTicker}
          />
        ),
      },
      {
        fieldName: "accountId",
        displayName: "Account Id",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.STRIPE &&
          (formValues.accountType === "EXISTING" || formValues.accountId),
        readOnly: ({ formValues }) => formValues.accountType === "NEW",
      },
      {
        fieldName: "accountLink",
        displayName: "Account Onboarding Link",
        helpText: "",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.STRIPE &&
          formValues.accountId &&
          formValues.accountType === "NEW",
        readOnly: true,
      },
      {
        fieldName: "accountLinkExpiresAt",
        widget: "hidden",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.STRIPE,
      },
      {
        fieldName: "stripeAccountLinkButton",
        widget: "custom",
        visible: ({ formValues }) => {
          const now = new Date();
          const expiresAt = new Date(formValues.accountLinkExpiresAt * 1000);
          const isExpired = expiresAt - now < 0;

          return (
            formValues.type === PAYMENT_TYPES.STRIPE &&
            formValues.accountId &&
            formValues.accountType === "NEW" &&
            isExpired
          );
        },
        customRenderer: ({ field, entity, updateFormValue }) => (
          <CreateAccountLinkBtn
            label={field.displayBtn}
            updateFormValue={updateFormValue}
            paymentMethod={entity.ticker}
            propertyTicker={entity.propertyTicker}
          />
        ),
      },
      {
        fieldName: "receiverHash",
        displayName: "Receiver hash",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS ||
          formValues.type === PAYMENT_TYPES.SEPTEO_VAULT,
      },
      {
        fieldName: "clientKey",
        displayName: "Client key",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS ||
          formValues.type === PAYMENT_TYPES.SEPTEO_VAULT ||
          formValues.type === PAYMENT_TYPES.SIPAY,
      },
      {
        fieldName: "notificationSecret",
        displayName: "Notification Secret",
        widget: "input",
        helpText: "This key is used only to verify payment notifications.",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS,
      },
      {
        fieldName: "septeoClientId",
        displayName: "Client ID",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS,
      },
      {
        fieldName: "clientSecret",
        displayName: "Client Secret",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.SIPAY ||
          formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS,
      },
      {
        fieldName: "resource",
        displayName: "Resource",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.SIPAY,
      },
      {
        fieldName: "googlePayMerchantId",
        displayName: "Google Pay Merchant ID",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.SIPAY || isPnpSdkExpress(formValues),
      },
      {
        fieldName: "componentType",
        displayName: "Component Type",
        widget: "autoComplete",
        closeMenuOnSelect: false,
        className: "w-64",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.STRIPE ||
          formValues.type === PAYMENT_TYPES.SEPTEO_PAYMENTS ||
          formValues.type === PAYMENT_TYPES.SIPAY ||
          formValues.type === PAYMENT_TYPES.PNP_SDK,
        transform: ({ entity, field }) => {
          switch (entity.type) {
            case PAYMENT_TYPES.SEPTEO_PAYMENTS:
              return {
                ...field,
                defaultValue: entity.properties.componentType || "dropIn",
                options: [
                  {
                    label: "Credit card payment",
                    value: "dropIn",
                  },
                  {
                    label: "Express payment",
                    value: "express",
                  },
                ],
              };

            case PAYMENT_TYPES.STRIPE:
            case PAYMENT_TYPES.SIPAY:
            default:
              return {
                ...field,
                defaultValue: entity.properties.componentType || "standard",
                options: [
                  {
                    label: "Payment Element",
                    value: "standard",
                  },
                  {
                    label: "Express Check-out Element",
                    value: "express",
                  },
                ],
              };
          }
        },
      },
      {
        fieldName: "title",
        displayName: "Title",
        widget: "input",
        translated: true,
        visible: ({ userInfo }) =>
          userInfo.isWitAdmin ||
          userInfo.isC2tAdmin ||
          userInfo.isCampingAdmin ||
          userInfo.isCampingFrAdmin ||
          userInfo.isAciAdmin,
      },
      {
        fieldName: "description",
        displayName: "Description",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ formValues, userInfo }) =>
          userInfo.isWitAdmin ||
          userInfo.isC2tAdmin ||
          userInfo.isCampingAdmin ||
          userInfo.isCampingFrAdmin ||
          userInfo.isAciAdmin ||
          formValues.type === PAYMENT_TYPES.TRANSFER ||
          formValues.type === PAYMENT_TYPES.WITHOUT_CARD ||
          formValues.type === PAYMENT_TYPES.RESERVATION_REQUEST,
      },
      {
        fieldName: "descriptionWithDeposit",
        displayName: "Description for deposit",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ userInfo }) =>
          userInfo.isWitAdmin ||
          userInfo.isC2tAdmin ||
          userInfo.isCampingAdmin ||
          userInfo.isCampingFrAdmin ||
          userInfo.isAciAdmin,
      },
      {
        fieldName: "descriptionWithoutDeposit",
        displayName: "Description Without Deposit",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ userInfo }) =>
          userInfo.isWitAdmin ||
          userInfo.isC2tAdmin ||
          userInfo.isCampingAdmin ||
          userInfo.isCampingFrAdmin ||
          userInfo.isAciAdmin,
      },
      {
        fieldName: "roomLockHours",
        displayName: "Availability blocking",
        widget: "input",
        helpText: "# de horas que guardamos la reserva para el cliente",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.TRANSFER ||
          formValues.type === PAYMENT_TYPES.RESERVATION_REQUEST,
      },
      {
        fieldName: "deferred",
        widget: "hidden",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.DEFERRED,
      },
      {
        fieldName: "automaticCancellation",
        displayName: "Automatically cancel all reservations that have expired blocking",
        widget: "checkbox",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.TRANSFER ||
          formValues.type === PAYMENT_TYPES.RESERVATION_REQUEST,
      },
      {
        fieldName: "service",
        displayName: "Service",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.PAY_NO_PAIN_V2 ||
          formValues.type === PAYMENT_TYPES.CHECKOUT_PNP ||
          formValues.type === PAYMENT_TYPES.PNP_SDK,
      },
      {
        fieldName: "apiKey",
        displayName: "API Key",
        widget: "input",
        visible: ({ formValues }) =>
          [
            PAYMENT_TYPES.PAY_NO_PAIN_V2,
            PAYMENT_TYPES.CRIPTAN,
            PAYMENT_TYPES.CHECKOUT_PNP,
            PAYMENT_TYPES.PAYCOMET,
            PAYMENT_TYPES.PAYCOMET_IVR,
            PAYMENT_TYPES.ADYEN,
            PAYMENT_TYPES.PNP_SDK,
          ].includes(formValues.type),
      },
      {
        fieldName: "hmacKey",
        displayName: "HMAC Key",
        widget: "input",
        helpText: "Required. Used to verify Adyen payment notifications.",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADYEN,
      },
      {
        fieldName: "prefix",
        displayName: "Production Prefix",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADYEN,
      },
      {
        fieldName: "installmentsIsActive",
        displayName: "Installments",
        widget: "checkbox",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.ADYEN,
        defaultValue: false,
      },
      {
        fieldName: "installmentsValues",
        displayName: "Installments Values",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.installmentsIsActive && formValues.type === PAYMENT_TYPES.ADYEN,
        helpText: "ex: 2,4,8,12",
      },
      {
        fieldName: "esKey",
        displayName: "ES Key",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.FREEDOM_PAY,
      },
      {
        fieldName: "storeId",
        displayName: "Store ID",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.FREEDOM_PAY,
      },
      {
        fieldName: "terminalId",
        displayName: "Terminal ID",
        widget: "input",
        visible: ({ formValues }) =>
          [PAYMENT_TYPES.PAYCOMET, PAYMENT_TYPES.PAYCOMET_IVR, PAYMENT_TYPES.FREEDOM_PAY].includes(
            formValues.type
          ),
      },
      {
        fieldName: "ivrProviderId",
        displayName: "IVR Provider ID",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PAYCOMET_IVR,
      },
      {
        fieldName: "signaturePassword",
        displayName: "Signature Password",
        widget: "input",
        helpText: "Required. Used to verify Paycomet payment notifications.",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.PAYCOMET ||
          formValues.type === PAYMENT_TYPES.PAYCOMET_IVR,
      },
      {
        fieldName: "ivrSessionTimeout",
        displayName: "IVR Session Timeout",
        widget: "selector",
        defaultValue: "300",
        optionDisplay: {
          300: {
            label: "5 min",
          },
          420: {
            label: "7 min",
          },
          600: {
            label: "10 min",
          },
        },
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PAYCOMET_IVR,
      },
      {
        fieldName: "signature",
        displayName: "Signature",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.PAY_NO_PAIN_V2 ||
          formValues.type === PAYMENT_TYPES.CHECKOUT_PNP ||
          formValues.type === PAYMENT_TYPES.PNP_SDK,
      },
      {
        fieldName: "template",
        displayName: "Template",
        widget: "input",
        visible: ({ formValues }) =>
          formValues.type === PAYMENT_TYPES.PAY_NO_PAIN_V2 ||
          formValues.type === PAYMENT_TYPES.CHECKOUT_PNP ||
          formValues.type === PAYMENT_TYPES.PNP_SDK,
      },
      {
        fieldName: "gatewayMerchantId",
        displayName: "Gateway Merchant Id",
        widget: "input",
        visible: ({ formValues }) => isPnpSdkExpress(formValues),
      },
      {
        fieldName: "paymentMethods",
        displayName: "Payment Methods",
        widget: "autoCompleteMulti",
        closeMenuOnSelect: false,
        className: "paymentMethodsSection",
        helpText: "*Si el campo está vacío, se aplicarán todos los métodos de pagos disponibles",
        visible: ({ formValues }) =>
          [
            PAYMENT_TYPES.CHECKOUT_PNP,
            PAYMENT_TYPES.PAYCOMET,
            PAYMENT_TYPES.ADDON_PAYMENTS,
            PAYMENT_TYPES.ADYEN,
            PAYMENT_TYPES.FREEDOM_PAY,
            PAYMENT_TYPES.PNP_SDK,
          ].includes(formValues.type),
        transform: ({ entity, field, formValues }) => {
          return {
            ...field,
            defaultValue:
              entity.properties.paymentMethods && entity.properties.paymentMethods.split(","),
            options: getPaymentMethodsOfPaymentType(
              formValues.type,
              selectValue(formValues.componentType)
            ),
          };
        },
      },
      {
        fieldName: "chargeDeposit",
        displayName: "chargeDeposit",
        widget: "hidden",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PAY_NO_PAIN_V2,
        defaultValue: true,
      },
      {
        fieldName: "multiAcquirers",
        displayName: "multiAcquirers",
        widget: "hidden",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.PAY_NO_PAIN_V2,
        defaultValue: false,
      },
      {
        fieldName: "agentId",
        displayName: "Agent Id",
        widget: "input",
        visible: ({ formValues }) => formValues.type === PAYMENT_TYPES.NEMURU,
      },
      {
        fieldName: "minDuration",
        displayName: "Min. release",
        widget: "input",
        helpText: "Si la antelación es menor no permite seleccionar este tipo de pago",
        className: "wbk-minDuration",
      },
      {
        fieldName: "minChronoUnit",
        widget: "hidden",
        defaultValue: "HOURS",
      },
      {
        fieldName: "maxDuration",
        displayName: "Max. release",
        widget: "input",
        helpText: "Si la antelación es mayor no permite seleccionar este tipo de pago",
        className: "wbk-maxDuration",
      },
      {
        fieldName: "codesSection",
        displayName: "Code",
        helpText:
          "Only when this code has been entered in the booking engine the payment method will activated.",
        className: "codesSection",
      },
      {
        fieldName: "codeType",
        displayName: "",
        widget: "selector",
        className: "codeType",
        defaultValue: CODE_TYPE.NO_CODE,
        optionDisplay: {
          [CODE_TYPE.NO_CODE]: {
            label: "No code",
          },
          [CODE_TYPE.UNIVERSAL_CODE]: {
            label: "Universal code",
          },
        },
      },
      {
        fieldName: RESTRICTION_TYPE.CODE,
        displayName: "",
        widget: "input",
        visible: ({ formValues }) => formValues.codeType !== CODE_TYPE.NO_CODE,
      },
      {
        fieldName: "maxChronoUnit",
        widget: "hidden",
        defaultValue: "HOURS",
      },
      {
        fieldName: RESTRICTION_TYPE.DEVICE_TYPE,
        displayName: "Tipo de dispositivo",
        widget: "multicheckbox",
        defaultValue: [],
        selectables: true,
        options: [
          {
            ticker: DEVICE_TYPE.DESKTOP,
            name: "Desktop",
          },
          {
            ticker: DEVICE_TYPE.TABLET,
            name: "Tablet",
          },
          {
            ticker: DEVICE_TYPE.MOBILE,
            name: "Móvil",
          },
        ],
        selectAllText: "Seleccionar Todo",
        selectNoneText: "Seleccionar Ninguno",
      },
    ],
  },
  validation: {
    type: {},
    receiver: {},
    notificationSecret: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    hmacKey: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    signaturePassword: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    merchantId: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    merchantPassword: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    merchantProductId: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    secret: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    rebatePassword: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    pnpClient: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    pnpGateway: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    key: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    clave256: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    Ds_Merchant_Currency: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    Ds_Merchant_MerchantCode: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    clientId: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    title: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    description: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    roomLockHours: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: -1,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    minRelease: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    maxRelease: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    paymentMethods: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    installmentsIsActive: {
      type: "Boolean",
      constraints: {},
    },
    installmentsValues: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
        format: {
          pattern: /^[0-9,]+$/,
          message: <FormattedMessage {...messages.installmentsValuesValidation} />,
        },
      },
    },
  },
};

// AutoCompleteGroup emits the whole {value,label} option object on change,
// while the persisted value is a plain string.
function selectValue(value) {
  return typeof value === "object" && value !== null ? value.value : value;
}

function isPnpSdkExpress(formValues) {
  return (
    formValues.type === PAYMENT_TYPES.PNP_SDK && selectValue(formValues.componentType) === "express"
  );
}

function getPaymentMethodsOfPaymentType(type, componentType) {
  switch (type) {
    case PAYMENT_TYPES.CHECKOUT_PNP:
      return [
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.BIZUM,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.BIZUM,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.GOOGLE_PAY,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.GOOGLE_PAY,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.APPLE_PAY,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.APPLE_PAY,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.IDEAL,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.IDEAL,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.SOFORT,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.SOFORT,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.PIX,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.PIX,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.CLICKTOPAY,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.CLICKTOPAY,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.CRYPTO,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.CRYPTO,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.KLARNA,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.KLARNA,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.GIROPAY,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.GIROPAY,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.MERCADO_PAGO,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.MERCADO_PAGO,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.PNP_V3,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.PNP_V3,
        },
        {
          label: CHECKOUT_PNP_PAYMENTS_METHODS_LABEL.VIACASH,
          value: CHECKOUT_PNP_PAYMENTS_METHODS.VIACASH,
        },
      ];
    case PAYMENT_TYPES.PAYCOMET:
      return [
        {
          label: PAYCOMET_PAYMENTS_METHODS_LABEL.CREDIT_CARDS,
          value: PAYCOMET_PAYMENTS_METHODS.CREDIT_CARDS,
        },
        { label: PAYCOMET_PAYMENTS_METHODS_LABEL.BIZUM, value: PAYCOMET_PAYMENTS_METHODS.BIZUM },
        { label: PAYCOMET_PAYMENTS_METHODS_LABEL.IDEAL, value: PAYCOMET_PAYMENTS_METHODS.IDEAL },
        { label: PAYCOMET_PAYMENTS_METHODS_LABEL.PAYPAL, value: PAYCOMET_PAYMENTS_METHODS.PAYPAL },
        { label: PAYCOMET_PAYMENTS_METHODS_LABEL.SOFORT, value: PAYCOMET_PAYMENTS_METHODS.SOFORT },
        {
          label: PAYCOMET_PAYMENTS_METHODS_LABEL.GIROPAY,
          value: PAYCOMET_PAYMENTS_METHODS.GIROPAY,
        },
        { label: PAYCOMET_PAYMENTS_METHODS_LABEL.KLARNA, value: PAYCOMET_PAYMENTS_METHODS.KLARNA },
      ];
    case PAYMENT_TYPES.ADDON_PAYMENTS:
      return [
        {
          label: ADDON_PAYMENTS_METHODS_LABEL.CREDIT_CARDS,
          value: ADDON_PAYMENTS_METHODS.CREDIT_CARDS,
        },
        { label: ADDON_PAYMENTS_METHODS_LABEL.SOFORT, value: ADDON_PAYMENTS_METHODS.SOFORT },
        { label: ADDON_PAYMENTS_METHODS_LABEL.IDEAL, value: ADDON_PAYMENTS_METHODS.IDEAL },
        { label: ADDON_PAYMENTS_METHODS_LABEL.GIROPAY, value: ADDON_PAYMENTS_METHODS.GIROPAY },
        { label: ADDON_PAYMENTS_METHODS_LABEL.BIZUM, value: ADDON_PAYMENTS_METHODS.BIZUM },
        { label: ADDON_PAYMENTS_METHODS_LABEL.APPLE_PAY, value: ADDON_PAYMENTS_METHODS.APPLE_PAY },
        { label: ADDON_PAYMENTS_METHODS_LABEL.KLARNA, value: ADDON_PAYMENTS_METHODS.KLARNA },
      ];
    case PAYMENT_TYPES.ADYEN:
      return [
        { label: ADYEN_METHODS_LABEL.CREDIT_CARDS_VISA, value: ADYEN_METHODS.CREDIT_CARDS_VISA },
        { label: ADYEN_METHODS_LABEL.CREDIT_CARDS_AMEX, value: ADYEN_METHODS.CREDIT_CARDS_AMEX },
        {
          label: ADYEN_METHODS_LABEL.CREDIT_CARDS_MASTER_CARD,
          value: ADYEN_METHODS.CREDIT_CARDS_MASTER_CARD,
        },
        { label: ADYEN_METHODS_LABEL.GOOGLE_PAY, value: ADYEN_METHODS.GOOGLE_PAY },
        { label: ADYEN_METHODS_LABEL.APPLE_PAY, value: ADYEN_METHODS.APPLE_PAY },
      ];
    case PAYMENT_TYPES.FREEDOM_PAY:
      return [
        {
          label: FREEDOM_PAY_PAYMENTS_METHODS_LABEL.CREDIT_CARDS,
          value: FREEDOM_PAY_PAYMENTS_METHODS.CREDIT_CARDS,
        },
        {
          label: FREEDOM_PAY_PAYMENTS_METHODS_LABEL.GOOGLE_PAY,
          value: FREEDOM_PAY_PAYMENTS_METHODS.GOOGLE_PAY,
        },
        {
          label: FREEDOM_PAY_PAYMENTS_METHODS_LABEL.APPLE_PAY,
          value: FREEDOM_PAY_PAYMENTS_METHODS.APPLE_PAY,
        },
      ];
    case PAYMENT_TYPES.PNP_SDK: {
      const methods = [
        {
          label: PNP_SDK_PAYMENTS_METHODS_LABEL.GOOGLE_PAY,
          value: PNP_SDK_PAYMENTS_METHODS.GOOGLE_PAY,
        },
        {
          label: PNP_SDK_PAYMENTS_METHODS_LABEL.APPLE_PAY,
          value: PNP_SDK_PAYMENTS_METHODS.APPLE_PAY,
        },
      ];
      // PayPal is only available in the standard modality; the engine silently
      // ignores it when componentType is "express".
      if (componentType !== "express") {
        methods.push({
          label: PNP_SDK_PAYMENTS_METHODS_LABEL.PAYPAL,
          value: PNP_SDK_PAYMENTS_METHODS.PAYPAL,
        });
        methods.push({
          label: PNP_SDK_PAYMENTS_METHODS_LABEL.PAYPAL_INSTALLMENTS,
          value: PNP_SDK_PAYMENTS_METHODS.PAYPAL_INSTALLMENTS,
        });
      }
      return methods;
    }
    default:
      throw new Error("invalid payment type for getting payment methods");
  }
}

export default ({ locale }) =>
  getTranslatedDefinition({ ModuleDefinition, ModuleTranslations, locale });
